import { useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import {
  Box, TextField, Button, ButtonGroup, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import { Send as SendIcon, Check as CheckIcon } from '@mui/icons-material';

interface MessageFormProps {
  ticketId: number;
}

export const MessageForm = ({ ticketId }: MessageFormProps) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSend = async (closeTicket: boolean = false) => {
    if (!text.trim()) {
      notify('Message cannot be empty', { type: 'warning' });
      return;
    }

    setLoading(true);

    try {
      await dataProvider.create(`support/tickets/${ticketId}/messages`, {
        data: { text, close_ticket: closeTicket }
      });

      setText('');
      notify(closeTicket ? 'Message sent and ticket closed' : 'Message sent', { type: 'success' });
      refresh();
      setConfirmDialogOpen(false);
    } catch (error: any) {
      notify(error?.message || 'Error sending message', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendAndClose = () => {
    if (!text.trim()) {
      notify('Message cannot be empty', { type: 'warning' });
      return;
    }
    setConfirmDialogOpen(true);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Type your message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={loading}
          variant="outlined"
        />
        <ButtonGroup orientation="vertical">
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSend(false)}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
          >
            Send
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSendAndClose}
            disabled={loading}
            startIcon={<CheckIcon />}
          >
            Send & Close
          </Button>
        </ButtonGroup>
      </Box>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Close Ticket?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to send this message and close the ticket?
            The user won't be able to reply unless they create a new ticket.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSend(true)}
            color="success"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Send & Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
